import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import clsx from "clsx";

import { Button, Tooltip } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import BoltIcon from "@material-ui/icons/FlashOn";
import EditNoteIcon from "@material-ui/icons/RateReview";
import TaskAltIcon from "@material-ui/icons/CheckCircleOutline";
import HeadsetIcon from "@material-ui/icons/HeadsetMic";
import HourglassIcon from "@material-ui/icons/HourglassEmpty";
import PriorityHighIcon from "@material-ui/icons/PriorityHigh";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import SupportAgentIcon from "@material-ui/icons/RecordVoiceOver";

import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { AEROS, contrastTextFor } from "../../styles/aerosTokens";
import NewTicketModal from "../../components/NewTicketModal";
import { useEntryStyles } from "./entryStyles";
import {
  LIMITE_SEM_RESPOSTA_MIN,
  agendaDeHoje,
  atencaoItems,
  conversasSemResposta,
  formatWait,
  greeting,
  initials,
  queueTone,
} from "./entryRules";

// Intervalo de atualização do painel. Os números são agregados, então uma
// releitura periódica é a base; por cima dela, um evento de ticket no socket
// agenda UMA releitura com atraso curto (várias mudanças em sequência viram
// uma consulta só). Sem isso, fechar um atendimento no "x" da lista deixava
// "Resolvidos hoje" parado por até um minuto e parecia não ter contado.
const REFRESH_MS = 60 * 1000;
const SOCKET_DEBOUNCE_MS = 1500;

const hoje = () => format(new Date(), "yyyy-MM-dd");

const DayPanel = () => {
  const classes = useEntryStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { setCurrentTicket } = useContext(TicketsContext);

  const [counters, setCounters] = useState({});
  const [queuePanel, setQueuePanel] = useState({ queues: [], totals: {} });
  const [agenda, setAgenda] = useState([]);
  const [openTickets, setOpenTickets] = useState([]);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [pulling, setPulling] = useState(false);

  const carregar = useCallback(async () => {
    const dia = hoje();

    // Cada bloco falha por conta própria: um erro no resumo de agendamentos
    // não pode apagar os KPIs e as filas da tela.
    const [dash, painel, resumo] = await Promise.allSettled([
      api.get("/dashboard", {
        params: { date_from: dia, date_to: dia, userId: user?.id },
      }),
      api.get("/tickets/queue-panel"),
      api.get("/schedules/summary"),
    ]);

    if (dash.status === "fulfilled") setCounters(dash.value.data?.counters || {});
    if (resumo.status === "fulfilled") {
      setAgenda(agendaDeHoje(resumo.value.data?.upcoming));
    }

    const dadosPainel =
      painel.status === "fulfilled"
        ? painel.value.data || { queues: [], totals: {} }
        : { queues: [], totals: {} };
    setQueuePanel(dadosPainel);

    // `GET /tickets` **exige** queueIds: sem o parâmetro o
    // `ListTicketsService` monta um `where` com a lista vazia e devolve zero
    // ticket, com status 200 — falha silenciosa. As filas do usuário vêm do
    // próprio queue-panel, então esta chamada depende dele.
    const filas = dadosPainel.queues.map((q) => q.id);
    if (!filas.length) {
      setOpenTickets([]);
      return;
    }

    try {
      const { data } = await api.get("/tickets", {
        params: {
          status: "open",
          pageNumber: 1,
          showAll: false,
          queueIds: JSON.stringify(filas),
        },
      });
      setOpenTickets(data?.tickets || []);
    } catch (err) {
      setOpenTickets([]);
    }
  }, [user]);

  useEffect(() => {
    carregar();
    const id = setInterval(carregar, REFRESH_MS);
    return () => clearInterval(id);
  }, [carregar]);

  const debounceRef = useRef(null);
  useEffect(() => {
    const companyId = user?.companyId;
    if (!socket || !companyId) return undefined;

    const onTicket = (data) => {
      if (!data?.ticket && data?.action !== "delete") return;
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(carregar, SOCKET_DEBOUNCE_MS);
    };
    socket.on(`company-${companyId}-ticket`, onTicket);
    return () => {
      clearTimeout(debounceRef.current);
      socket.off(`company-${companyId}-ticket`, onTicket);
    };
  }, [socket, user, carregar]);

  const aguardando = queuePanel?.totals?.waiting ?? 0;
  const maisAntigo = formatWait(queuePanel?.totals?.oldestWaitSeconds);
  const paradas = conversasSemResposta(openTickets, user?.id);
  const semResposta = paradas.length;
  const atencao = atencaoItems({ semResposta });

  /**
   * Clique em "Precisa de atenção": abre a conversa parada há mais tempo. A
   * primeira versão navegava para `/tickets`, que é a própria tela — o clique
   * não fazia nada.
   */
  const abrirMaisParada = () => {
    const alvo = paradas[0];
    if (alvo?.uuid) history.push(`/tickets/${alvo.uuid}`);
  };

  /**
   * "Puxar próximo": abre o pendente que espera há mais tempo nas filas do
   * usuário. Só navega até ele — quem assume é o botão do cartão de contexto,
   * para que o atendente veja de quem se trata antes de entrar na conversa.
   */
  const puxarProximo = async () => {
    const filas = (queuePanel.queues || []).map((q) => q.id);
    if (!filas.length) {
      toast.info(i18n.t("aeros.ticketsEntry.pullNextEmpty"));
      return;
    }

    setPulling(true);
    try {
      // `sortTickets: "ASC"` traz o mais antigo primeiro, que é o que "puxar
      // próximo" promete. `queueIds` é obrigatório — ver o comentário em
      // `carregar`.
      const { data } = await api.get("/tickets", {
        params: {
          status: "pending",
          pageNumber: 1,
          showAll: false,
          sortTickets: "ASC",
          queueIds: JSON.stringify(filas),
        },
      });
      const proximo = (data?.tickets || [])[0];
      if (!proximo) {
        // Lista vazia não é erro: `toastError` descartaria este texto e
        // mostraria "An error occurred!".
        toast.info(i18n.t("aeros.ticketsEntry.pullNextEmpty"));
        return;
      }
      history.push(`/tickets/${proximo.uuid}`);
    } catch (err) {
      toastError(err);
    } finally {
      setPulling(false);
    }
  };

  /**
   * "Atender" na linha da fila: abre o pendente mais antigo **daquela** fila.
   *
   * A primeira versão navegava para `/tickets?queueId=X&status=pending`, mas
   * a tela não lê filtro por query string — o clique não fazia nada visível.
   */
  const abrirFila = async (queueId) => {
    try {
      const { data } = await api.get("/tickets", {
        params: {
          status: "pending",
          pageNumber: 1,
          showAll: false,
          sortTickets: "ASC",
          queueIds: JSON.stringify([queueId]),
        },
      });
      const proximo = (data?.tickets || [])[0];
      if (!proximo) {
        toast.info(i18n.t("aeros.ticketsEntry.pullNextEmpty"));
        return;
      }
      history.push(`/tickets/${proximo.uuid}`);
    } catch (err) {
      toastError(err);
    }
  };

  const corDaFila = (q, indice) =>
    q.color || AEROS.avatarCycle[indice % AEROS.avatarCycle.length];

  const toneColor = {
    urgent: AEROS.error,
    warn: AEROS.warning,
    normal: theme.palette.text.primary,
    idle: theme.palette.aeros.textTertiary,
  };

  const avatarBg = AEROS.avatarCycle[(user?.id || 0) % AEROS.avatarCycle.length];

  return (
    <div className={classes.root}>
      {newTicketOpen && (
        <NewTicketModal
          modalOpen={newTicketOpen}
          onClose={(ticket) => {
            setNewTicketOpen(false);
            if (ticket?.uuid) {
              setCurrentTicket(ticket);
              history.push(`/tickets/${ticket.uuid}`);
            }
          }}
        />
      )}

      <div className={classes.header}>
        <div
          className={classes.headerAvatar}
          style={{ background: avatarBg, color: contrastTextFor(avatarBg) }}
        >
          {initials(user?.name)}
        </div>
        <div className={classes.headerTexts}>
          <h1 className={classes.headerTitle}>{greeting(user?.name)}</h1>
          <div className={classes.headerSubtitle}>
            {format(new Date(), "dd/MM/yyyy")}
            {" · "}
            {i18n.t("aeros.ticketsEntry.subtitle.waiting", { count: aguardando })}
          </div>
        </div>
        <div className={classes.headerActions}>
          <Button
            className={classes.ghostButton}
            startIcon={<EditNoteIcon style={{ fontSize: 17 }} />}
            onClick={() => setNewTicketOpen(true)}
          >
            {i18n.t("aeros.ticketsEntry.newConversation")}
          </Button>
          <Button
            className={classes.gradientButton}
            startIcon={<BoltIcon style={{ fontSize: 18 }} />}
            disabled={pulling || !aguardando}
            onClick={puxarProximo}
          >
            {i18n.t("aeros.ticketsEntry.pullNext")}
          </Button>
        </div>
      </div>

      <div className={classes.body}>
        <div className={classes.kpiGrid}>
          <Kpi
            classes={classes}
            icon={<TaskAltIcon className={classes.kpiIcon} style={{ color: AEROS.success }} />}
            label={i18n.t("aeros.ticketsEntry.kpi.solvedToday")}
            value={counters.supportFinished ?? 0}
          />
          <Kpi
            classes={classes}
            icon={<HeadsetIcon className={classes.kpiIcon} style={{ color: AEROS.cyan }} />}
            label={i18n.t("aeros.ticketsEntry.kpi.inService")}
            value={counters.supportHappening ?? 0}
            hint={
              semResposta > 0
                ? i18n.t("aeros.ticketsEntry.kpi.noReplyHint", {
                    count: semResposta,
                    minutes: LIMITE_SEM_RESPOSTA_MIN,
                  })
                : null
            }
          />
          <Kpi
            classes={classes}
            alert={aguardando > 0}
            icon={<HourglassIcon className={classes.kpiIcon} style={{ color: AEROS.warning }} />}
            label={i18n.t("aeros.ticketsEntry.kpi.waiting")}
            value={aguardando}
            valueColor={aguardando > 0 ? AEROS.warning : undefined}
            hint={
              maisAntigo
                ? i18n.t("aeros.ticketsEntry.kpi.oldestHint", { wait: maisAntigo })
                : null
            }
          />
        </div>

        <div className={classes.columns}>
          {/* ---------- Suas filas agora ---------- */}
          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <div className={classes.cardTitle}>
                {i18n.t("aeros.ticketsEntry.queues.title")}
              </div>
              <Button
                className={classes.cardAction}
                onClick={() => history.push("/queues")}
              >
                {i18n.t("aeros.ticketsEntry.queues.seeAll")}
              </Button>
            </div>

            {!queuePanel.queues?.length && (
              <div className={classes.emptyLine}>
                {i18n.t("aeros.ticketsEntry.queues.empty")}
              </div>
            )}

            {queuePanel.queues?.map((q, i) => {
              const tone = queueTone(q.waiting, q.oldestWaitSeconds);
              const cor = corDaFila(q, i);
              const espera = formatWait(q.oldestWaitSeconds);
              return (
                <div className={classes.queueRow} key={q.id}>
                  <div
                    className={classes.queueTile}
                    style={{ background: `${cor}24` }}
                  >
                    <SupportAgentIcon style={{ fontSize: 18, color: cor }} />
                  </div>
                  <div className={classes.queueTexts}>
                    <div className={classes.queueName}>{q.name}</div>
                    <div className={classes.queueDetail}>
                      {espera
                        ? i18n.t("aeros.ticketsEntry.queues.detail", {
                            wait: espera,
                            agents: q.onlineAgents,
                            count: q.onlineAgents,
                          })
                        : i18n.t("aeros.ticketsEntry.queues.detailIdle", {
                            agents: q.onlineAgents,
                            count: q.onlineAgents,
                          })}
                    </div>
                  </div>
                  <div className={classes.queueCount}>
                    <div
                      className={classes.queueCountValue}
                      style={{ color: toneColor[tone] }}
                    >
                      {q.waiting}
                    </div>
                    <div className={classes.queueCountLabel}>
                      {i18n.t("aeros.ticketsEntry.queues.waiting")}
                    </div>
                  </div>
                  <Button
                    className={clsx(classes.queueButton, {
                      [classes.queueButtonPrimary]: tone === "urgent",
                    })}
                    disabled={!q.waiting}
                    onClick={() => abrirFila(q.id)}
                  >
                    {i18n.t("aeros.ticketsEntry.queues.answer")}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* ---------- Agenda + Precisa de atenção ---------- */}
          <div className={classes.sideColumn}>
            <div className={clsx(classes.card, classes.cardPadded, classes.stack)}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <div className={classes.cardTitle}>
                  {i18n.t("aeros.ticketsEntry.agenda.title")}
                </div>
                {agenda.length > 0 && (
                  <div className={classes.cardCount}>{agenda.length}</div>
                )}
              </div>

              {!agenda.length && (
                <div className={classes.queueDetail}>
                  {i18n.t("aeros.ticketsEntry.agenda.empty")}
                </div>
              )}

              {agenda.map((s, i) => {
                const cor = AEROS.avatarCycle[i % AEROS.avatarCycle.length];
                return (
                  <div className={classes.agendaRow} key={s.id ?? i}>
                    <div className={classes.agendaTime} style={{ color: cor }}>
                      {format(new Date(s.sendAt), "HH:mm")}
                    </div>
                    <div className={classes.agendaBar} style={{ background: cor }} />
                    <div className={classes.agendaTexts}>
                      <div className={classes.agendaContact}>
                        {s.contact?.name || "—"}
                      </div>
                      <Tooltip title={s.body || ""}>
                        <div className={classes.agendaWhat}>{s.body}</div>
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </div>

            {atencao.length > 0 && (
              <div
                className={clsx(classes.card, classes.cardPadded, classes.stackTight)}
              >
                <div className={classes.cardTitle}>
                  {i18n.t("aeros.ticketsEntry.attention.title")}
                </div>
                {atencao.map((item) => (
                  <Button
                    key={item.key}
                    className={classes.attentionRow}
                    style={{
                      background: "rgba(248,113,113,0.07)",
                      border: "1px solid rgba(248,113,113,0.25)",
                    }}
                    onClick={abrirMaisParada}
                  >
                    <PriorityHighIcon style={{ fontSize: 17, color: AEROS.error }} />
                    <span className={classes.attentionText}>{item.text}</span>
                    <ChevronRightIcon
                      style={{ fontSize: 16, color: theme.palette.aeros.textTertiary }}
                    />
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Kpi = ({ classes, icon, label, value, hint, valueColor, alert }) => (
  <div className={clsx(classes.kpiCard, { [classes.kpiCardAlert]: alert })}>
    <div className={classes.kpiTop}>
      {icon}
      <div className={classes.kpiLabel}>{label}</div>
    </div>
    <div className={classes.kpiValue} style={valueColor ? { color: valueColor } : undefined}>
      {value}
    </div>
    <div className={classes.kpiHint}>{hint}</div>
  </div>
);

export default DayPanel;
